/**
 * Firebase initialization for Admin Portal
 * Uses the same Firebase project as GUS (silacitiesrcrcapp)
 */
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, OAuthProvider, Auth } from 'firebase/auth';
import firebaseConfig, { validateFirebaseConfig, isDevelopment, isAllowedEmail } from './config/firebase.config';

// Validate configuration before initializing
if (!validateFirebaseConfig() && !isDevelopment) {
  throw new Error('Invalid Firebase configuration. Please check environment variables.');
}

// Log configuration status (without exposing sensitive data)
if (isDevelopment) {
  console.log('Firebase initialization:', {
    hasApiKey: !!firebaseConfig.apiKey,
    hasAuthDomain: !!firebaseConfig.authDomain,
    hasProjectId: !!firebaseConfig.projectId,
    environment: process.env.NODE_ENV
  });
}

// Initialize Firebase
let app: FirebaseApp;
if (!getApps().length) {
  try {
    app = initializeApp(firebaseConfig);
    if (isDevelopment) {
      console.log('Firebase initialized successfully');
    }
  } catch (error) {
    console.error('Firebase initialization error:', error);
    if (!isDevelopment) {
      throw error;
    }
    app = {} as FirebaseApp; // Fallback for dev
  }
} else {
  app = getApps()[0];
  if (isDevelopment) {
    console.log('Firebase already initialized');
  }
}

// Export Firebase Authentication
export const auth: Auth = getAuth(app);

// Export Google Auth Provider
export const googleProvider = new GoogleAuthProvider();

// Add custom claims to Google provider for better security
googleProvider.addScope('profile');
googleProvider.addScope('email');

// Export Microsoft Auth Provider
export const microsoftProvider = new OAuthProvider('microsoft.com');

// Configure Microsoft provider for both personal and work accounts
microsoftProvider.setCustomParameters({
  tenant: 'common', // Use 'common' for personal & work accounts
});

// Add scopes for Microsoft
microsoftProvider.addScope('profile');
microsoftProvider.addScope('email');

// Helper function to get current user token for API calls
export const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  try {
    const token = await user.getIdToken(forceRefresh);
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// Re-export isAllowedEmail for convenience
export { isAllowedEmail };

// Export the app instance
export default app;
